import React, {
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
  useContext,
} from "react";
import navSchema from "./NavStructure";
import { Link, navigate } from "gatsby";
import { useLocation } from "@reach/router";
import useDeviceSize from "../../../../../hooks/useDeviceSize";
import ExternalLink from "../../../../external-link";
import { appContext } from "providers/appProvider";

const getNav = (navList, parent) =>
  navList.filter((item) => item.parent === parent);
const getSubNavChildren = (navList, parent) => {
  let navResult = navList.filter((item) => item.parent === parent);
  return navResult.length > 0 ? navResult : null;
};

const MainNav = ({ closeHamburgerMenu }) => {
  const [currentHover, setCurrentHover] = useState(null);
  const [submenuClicked, setSubmenuClicked] = useState(false);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const menuTimeout = useRef(null);
  const lastInteractionType = useRef("mouse");
  const location = useLocation();
  const { appConfigs } = useContext(appContext);

  const showSubmenu = (id) => {
    setCurrentHover(id);
    clearTimeout(menuTimeout.current);
  };

  const hideSubmenu = () => {
    setCurrentHover(null);
    setFocusedIndex(null);
  };

  const handleMouseEnter = (id) => showSubmenu(id);
  const handleMouseLeave = () => {
    menuTimeout.current = setTimeout(hideSubmenu, 0);
  };

  const handleFocus = (id) => {
    if (lastInteractionType.current === "keyboard") {
      setIsKeyboardNav(true);
    }
    showSubmenu(id);
  };

  const handleBlur = () => {
    menuTimeout.current = setTimeout(hideSubmenu, 100);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Tab") {
        lastInteractionType.current = "keyboard";
        setIsKeyboardNav(true);
      }
    };

    const handleMouseDown = () => {
      lastInteractionType.current = "mouse";
      setIsKeyboardNav(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  useEffect(() => {
    if (submenuClicked) {
      setTimeout(() => {
        setSubmenuClicked(false);
      }, 1);
    }
  }, [submenuClicked]);

  const handleSubmenuItemClick = useCallback(
    (e, link) => {
      // Check if link is defined and prevent default if it's a hash link to handle the navigation manually
      if (link && link.startsWith("#")) {
        e.preventDefault();
        // Set the submenu as clicked and navigate if needed
        setSubmenuClicked(true);
        // Since it's a hash link, we just want to scroll to the section
        // The actual navigation (scrolling) logic would go here
      } else if (link) {
        // For non-hash links, we navigate normally
        hideSubmenu();
        closeHamburgerMenu();
        navigate(link);
      }
      // If link is not defined, handle accordingly, maybe log an error or ignore
    },
    [closeHamburgerMenu]
  );

  const handleKeyDown = (e, item) => {
    e.stopPropagation();
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (item.navLink) {
        navigate(item.navLink);
      }
    } else if (e.key === "ArrowDown" && currentHover === item.id) {
      e.preventDefault();
      const submenu = document.querySelector(`#submenu-${item.id}`);
      if (submenu) {
        setFocusedIndex(0);
        submenu.querySelector("a").focus();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      hideSubmenu();
    }
  };

  const handleSubMenuKeyDown = (e, items) => {
    e.stopPropagation();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prevIndex) =>
        prevIndex === null || prevIndex === items.length - 1 ? 0 : prevIndex + 1
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prevIndex) =>
        prevIndex === null || prevIndex === 0 ? items.length - 1 : prevIndex - 1
      );
    } else if (e.key === "Escape") {
      e.preventDefault();
      hideSubmenu();
    }
  };

  useEffect(() => {
    if (currentHover !== null && focusedIndex !== null) {
      const submenu = document.querySelector(`#submenu-${currentHover}`);
      if (submenu) {
        const focusedItem = submenu.querySelectorAll("a")[focusedIndex];
        if (focusedItem) {
          focusedItem.focus();
        }
      }
    }
  }, [currentHover, focusedIndex]);

  const topLevelItems = getNav(navSchema, null);

  return (
    <nav aria-label="Main menu">
      <ul>
        {topLevelItems.map((item) => {
          const hasSubNav = getSubNavChildren(navSchema, item.id);
          return (
            <li
              key={item.id}
              className={`subnav hasdropdown ${
                currentHover === item.id ? "hover" : ""
              }`}
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}
              onFocus={() => handleFocus(item.id)}
              onBlur={handleBlur}
            >
              <Link
                to={item.navLink}
                dangerouslySetInnerHTML={{ __html: item.title }}
                activeClassName="active"
                partiallyActive={true}
                className={isKeyboardNav ? "keyboard-nav" : ""}
                onClick={handleSubmenuItemClick}
                aria-current={
                  item.navLink === location.pathname ? "page" : undefined
                }
                aria-expanded={currentHover === item.id ? "true" : "false"}
                aria-controls={hasSubNav ? `submenu-${item.id}` : undefined}
                onKeyDown={(e) => handleKeyDown(e, item)}
                role={hasSubNav ? "button" : undefined}
              />
              {hasSubNav && (
                <SubNavigation
                  itemId={item.id}
                  currentHover={currentHover}
                  isKeyboardNav={isKeyboardNav}
                  focusedIndex={focusedIndex}
                  closeHamburgerMenu={closeHamburgerMenu}
                  hideSubmenu={hideSubmenu}
                  onSubmenuItemClick={handleSubmenuItemClick}
                  onSubMenuKeyDown={handleSubMenuKeyDown}
                />
              )}
            </li>
          );
        })}
      </ul>
      <div className="bottom-menu">
        <ul>
          <li>
            <Link to="#isiContainer" onClick={closeHamburgerMenu}>
              Important Safety Information
            </Link>
          </li>
          <li>
            <ExternalLink
              externalURL="https://dailymed.nlm.nih.gov/dailymed/fda/fdaDrugXsl.cfm?setid=3abfbffc-9e52-4fc6-ae92-6e0a97f4afe7&type=display"
              messageTitle={appConfigs.externalLinkInfoPI.TITLE}
              messageBody={appConfigs.externalLinkInfoPI.BODY}
              classNameLink="menu-link"
              classNameOverlay={
                appConfigs.externalLinkInfoPI.OVERLAY_CLASS_NAME
              }
            >
              Full Prescribing Information
            </ExternalLink>
          </li>
        </ul>
      </div>
    </nav>
  );
};

const SubNavigation = ({
  itemId,
  currentHover,
  isKeyboardNav,
  focusedIndex,
  onSubmenuItemClick,
  onSubMenuKeyDown,
}) => {
  const subNavItems = getSubNavChildren(navSchema, itemId);
  const deviceSizes = useDeviceSize();
  const location = useLocation();

  // This function determines if a link is the active link
  const isActiveLink = (navLink) => {
    // Split the navLink into path and hash for comparison
    const [path, hash] = navLink.split("#");
    // Compare path and hash separately for accuracy
    return location.pathname === path && location.hash === `#${hash}`;
  };

  // Handles submenu item click events with useCallback for optimization
  const handleSubMenuClick = useCallback(
    (e, link) => {
      if (deviceSizes.lgDown) {
        e.preventDefault();
        // Split the link for separate navigation logic
        const linkArr = link.split("#");
        if (location.pathname.includes(linkArr[0])) {
          navigate(link);
        } else {
          navigate(linkArr[0], { state: { sectionId: linkArr[1] } });
        }
      }
      onSubmenuItemClick(e, link);
    },
    [deviceSizes, location, onSubmenuItemClick]
  );

  return (
    <ul
      id={`submenu-${itemId}`}
      className={`level-2 ${currentHover === itemId ? "visible" : ""}`}
      aria-hidden={currentHover !== itemId}
    >
      {subNavItems.map((item) => (
        <li key={item.id}>
          <Link
            to={item.navLink}
            className={`submenu-item ${
              isActiveLink(item.navLink) ? "active" : ""
            } ${isKeyboardNav ? "keyboard-nav" : ""}`}
            onClick={(e) => handleSubMenuClick(e, item.navLink)}
            tabIndex={currentHover === itemId ? "0" : "-1"}
            onKeyDown={(e) => onSubMenuKeyDown(e, subNavItems)}
            aria-label={item.title}
          >
            {item.title}
          </Link>
        </li>
      ))}
    </ul>
  );
};

export default memo(MainNav);
